import { useAppMapMarkers } from "@/hooks/useAppMapMarkers";
import { useProximityRings } from "@/hooks/useProximityRings";
import { Map, useMap } from "@vis.gl/react-google-maps";
import { MapPinOff } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "components/ui/empty";
import { Spinner } from "components/ui/spinner";
import { ClusteredMarkers } from "components/AppMap/ClusteredMarkers";
import { ProximityRings } from "components/ProximityRings";
import { ToggleProximityRingsButton } from "components/ProximityRings/ToggleProximityRingsButton";
import { Badge } from "components/ui/badge";

const containerStyle = {
  width: "100%",
  height: "calc(90vh - 120px)",
};

export default function AppMap({
  modelId,
  modelType,
  locationType,
  hasAddresses,
  locations,
}) {
  const { markers, isLoading } = useAppMapMarkers({
    modelId,
    modelType,
    locationType,
    hasAddresses,
    locations,
  });

  const map = useMap();
  const [ringsVisible, setRingsVisible] = useState(true);

  // Only the address markers anchor rings; an event or location pin is one of
  // the things being measured, not the thing measured from.
  const addressCenters = useMemo(
    () =>
      markers.data
        .filter(marker => marker.type === "address")
        .map(marker => marker.position),
    [markers]
  );

  const proximityRings = useProximityRings({ centers: addressCenters });

  const toggleRings = useCallback(
    () => setRingsVisible(isVisible => !isVisible),
    []
  );

  useEffect(() => {
    if (map && markers.bounds) {
      map.fitBounds(markers.bounds);
    }
  }, [map, markers]);

  if (markers.data.length === 0 && !isLoading) {
    return (
      <div
        style={containerStyle}
        className="tw:flex tw:items-center tw:justify-center"
      >
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <MapPinOff />
            </EmptyMedia>
            <EmptyTitle>No locations found.</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div className="tw:relative tw:w-full tw:h-full">
      <Map
        defaultZoom={3}
        defaultCenter={{ lat: 22.54992, lng: 0 }}
        gestureHandling={"greedy"}
        mapId={"49c70c2f29feecc1b45bc70f"}
        style={containerStyle}
      >
        <ClusteredMarkers geojson={markers.geoJson} />
        <ProximityRings
          geoJson={proximityRings.geoJson}
          visible={ringsVisible}
        />
      </Map>
      {proximityRings.available && (
        <div className="tw:absolute tw:bottom-36 tw:right-2 tw:rounded-lg tw:border tw:border-(--color-border) tw:bg-(--color-surface)/90 tw:p-1 tw:shadow-md tw:backdrop-blur-sm">
          <ToggleProximityRingsButton
            isShowing={ringsVisible}
            onToggle={toggleRings}
          />
        </div>
      )}
      {isLoading ? (
        <Badge
          className="tw:absolute tw:bottom-1 tw:left-1/2 tw:-translate-x-1/2"
          variant="secondary"
        >
          <Spinner data-icon="inline-start" />
          Loading
        </Badge>
      ) : undefined}
    </div>
  );
}
